/**
 * Display units and position format, kept ON the device (localStorage) so the
 * skipper's choices survive a reload. These change how numbers are SHOWN, not how
 * they're stored: everything is held internally in the base units (metres,
 * knots, nautical miles, WGS84 degrees) and converted for display at the edge.
 *
 * Position format defaults to degrees-minutes-decimal-minutes (DMm), because
 * that's how paper charts and RYA courses work; DMS and decimal degrees are
 * offered for anyone who prefers them.
 */
import { create } from 'zustand';

export type DepthUnit = 'm' | 'ft';
export type DistanceUnit = 'nm' | 'km';
export type SpeedUnit = 'kn' | 'kmh';
export type PositionFormat = 'dmm' | 'dms' | 'dd';

export interface UnitsPrefs {
  depth: DepthUnit;
  distance: DistanceUnit;
  speed: SpeedUnit;
  position: PositionFormat;
}

export const DEFAULT_UNITS: UnitsPrefs = {
  depth: 'm',
  distance: 'nm',
  speed: 'kn',
  position: 'dmm',
};

const KEY = 'acp.units.v1';

function loadPersisted(): UnitsPrefs {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    if (!raw) return { ...DEFAULT_UNITS };
    const d = JSON.parse(raw) as Partial<UnitsPrefs>;
    const prefs = { ...DEFAULT_UNITS, ...d };
    // mph was retired as a speed unit; fall back to knots if it was ever saved.
    if ((prefs.speed as string) === 'mph') prefs.speed = 'kn';
    return prefs;
  } catch {
    return { ...DEFAULT_UNITS };
  }
}

function persist(s: UnitsPrefs): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore quota / private-mode / SSR */
  }
}

interface UnitsState extends UnitsPrefs {
  /** Patch one or more preferences and persist. */
  set: (patch: Partial<UnitsPrefs>) => void;
}

const initial = loadPersisted();

export const useUnitsStore = create<UnitsState>((set, get) => ({
  ...initial,
  set: (patch) => {
    const next: UnitsPrefs = {
      depth: get().depth,
      distance: get().distance,
      speed: get().speed,
      position: get().position,
      ...patch,
    };
    persist(next);
    set(next);
  },
}));

// --- Conversions and labels (pure; used by formatters and the data fields) ---

const M_TO_FT = 3.280839895;
const NM_TO_KM = 1.852;
const KN_TO_KMH = 1.852;

/** Convert a depth/height in metres to the chosen unit. */
export function toDepth(metres: number, unit: DepthUnit): number {
  return unit === 'ft' ? metres * M_TO_FT : metres;
}
export function depthUnitLabel(unit: DepthUnit): string {
  return unit === 'ft' ? 'ft' : 'm';
}

/**
 * The one place a depth or height is turned into text for display. Everything is
 * held in metres and converted here at the edge, so a single toggle (units.depth)
 * swings every readout together and nothing drifts to its own format.
 *
 * The value is converted to the chosen unit, then rounded and suffixed with one
 * space, e.g. "1.5 m" or "5 ft". Metres default to one decimal (the depth
 * precision the charts and RYA work in); feet default to whole feet, since a
 * foot is finer than a decimetre and the extra decimal just adds noise. A caller
 * that needs a different precision passes `decimals` to override both.
 */
export function formatDepth(metres: number, unit: DepthUnit, decimals?: number): string {
  const dp = decimals ?? (unit === 'ft' ? 0 : 1);
  return `${toDepth(metres, unit).toFixed(dp)} ${depthUnitLabel(unit)}`;
}

/** Convert a distance in nautical miles to the chosen unit. */
export function toDistance(nm: number, unit: DistanceUnit): number {
  return unit === 'km' ? nm * NM_TO_KM : nm;
}
export function distanceUnitLabel(unit: DistanceUnit): string {
  return unit === 'km' ? 'km' : 'nm';
}

/** Convert a speed in knots to the chosen unit. */
export function toSpeed(kn: number, unit: SpeedUnit): number {
  if (unit === 'kmh') return kn * KN_TO_KMH;
  return kn;
}
export function speedUnitLabel(unit: SpeedUnit): string {
  if (unit === 'kmh') return 'km/h';
  return 'kn';
}
